/**
 * Questions, plans and MCP elicitations as approvals (module 'approvals', R-HL-6): what
 * Claude Code asks, read from a hook's input, and a person's answer checked against it. The hub
 * runs both; the console may run checkAskAnswer to say what is wrong before it sends.
 *
 *   AskUserQuestion  held at PreToolUse         kind 'question'     answered with answers
 *   ExitPlanMode     held at PermissionRequest  kind 'plan'         approved (planMode) or sent back
 *   Elicitation      held at its own hook       kind 'elicitation'  accepted with content, or declined
 *
 * Each is held for a person at one event only (askHeldAt); at the other it passes at once, so
 * the same ask is never held twice. Claude Code answers AskUserQuestion only from PreToolUse
 * (allow with updatedInput: the questions as asked plus answers), and approves a plan from
 * PermissionRequest (allow, optionally setMode). Nobody answering leaves it to the session.
 *
 * Browser-safe and pure.
 */
import type { ApprovalAsk, ApprovalAskAnswer, AskQuestion, ElicitationField, ElicitationSchema, PlanContinueMode } from './approvals.ts';

/** The approval kinds a person answers with more than allow or deny. */
export type AskKind = 'question' | 'plan' | 'elicitation';

/** Claude Code tools whose permission request is a question or a plan, not a tool call. */
export const ASK_TOOLS: Readonly<Record<string, AskKind>> = { AskUserQuestion: 'question', ExitPlanMode: 'plan' };

/** The longest answer to one question (free text included). */
export const ASK_ANSWER_MAX_CHARS = 2000;
/** The longest plan kept on a request (a longer one is cut, its file has the rest). */
export const ASK_PLAN_MAX_CHARS = 40_000;
const MESSAGE_MAX_CHARS = 4000;
const PLAN_MODES: readonly PlanContinueMode[] = ['default', 'acceptEdits'];

/** The hook event at which an ask of this kind is held for a person. */
export function askHeldAt(kind: AskKind): 'PreToolUse' | 'PermissionRequest' | 'Elicitation' {
  return kind === 'question' ? 'PreToolUse' : kind === 'plan' ? 'PermissionRequest' : 'Elicitation';
}

export function isAskKind(kind: string | undefined): kind is AskKind {
  return kind === 'question' || kind === 'plan' || kind === 'elicitation';
}

/** The kind a hook call asks for: a question, a plan, an elicitation, or null for a tool call. */
export function askKindOf(event: string, tool: string): AskKind | null {
  if (event === 'Elicitation') return 'elicitation';
  return ASK_TOOLS[tool] ?? null;
}

const isObj = (x: unknown): x is Record<string, unknown> => !!x && typeof x === 'object' && !Array.isArray(x);
const text = (v: unknown): string => (typeof v === 'string' ? v : '');
const oneLine = (s: string, max: number): string => {
  const t = s.replace(/\s+/g, ' ').trim();
  return t.length <= max ? t : `${t.slice(0, Math.max(0, max - 1)).trimEnd()}…`;
};

function questionsOf(input: Record<string, unknown>): AskQuestion[] | null {
  if (!Array.isArray(input.questions) || input.questions.length === 0 || input.questions.length > 10) return null;
  const out: AskQuestion[] = [];
  const seen = new Set<string>();
  for (const q of input.questions) {
    if (!isObj(q) || !text(q.question).trim() || !Array.isArray(q.options)) return null;
    const question = text(q.question);
    // Answers are keyed by the question text: two the same could not be told apart.
    if (seen.has(question)) return null;
    seen.add(question);
    const options: AskQuestion['options'] = [];
    for (const o of q.options.slice(0, 20)) {
      if (!isObj(o) || !text(o.label)) return null;
      options.push({ label: text(o.label), ...(text(o.description) ? { description: text(o.description) } : {}) });
    }
    out.push({ question, header: text(q.header), options, multiSelect: q.multiSelect === true });
  }
  return out;
}

function fieldOf(raw: unknown): ElicitationField | null {
  if (!isObj(raw)) return null;
  const f: ElicitationField = { type: 'string' };
  // Titled choices (MCP 2025-11): oneOf [{ const, title }].
  const choices = Array.isArray(raw.oneOf) ? raw.oneOf : Array.isArray(raw.anyOf) ? raw.anyOf : null;
  if (choices) {
    if (!choices.every((c) => isObj(c) && typeof c.const === 'string')) return null;
    f.enum = choices.map((c) => String((c as Record<string, unknown>).const));
    f.enumNames = choices.map((c) => text((c as Record<string, unknown>).title) || String((c as Record<string, unknown>).const));
  } else if (raw.type === 'string' || raw.type === 'number' || raw.type === 'integer' || raw.type === 'boolean') {
    f.type = raw.type;
  } else if (raw.type !== undefined || !Array.isArray(raw.enum)) return null;
  if (Array.isArray(raw.enum)) {
    if (!raw.enum.every((v) => typeof v === 'string')) return null;
    f.type = 'string';
    f.enum = raw.enum as string[];
    if (Array.isArray(raw.enumNames) && raw.enumNames.length === raw.enum.length && raw.enumNames.every((v) => typeof v === 'string')) f.enumNames = raw.enumNames as string[];
  }
  if (text(raw.title)) f.title = text(raw.title);
  if (text(raw.description)) f.description = text(raw.description);
  if (typeof raw.default === 'string' || typeof raw.default === 'number' || typeof raw.default === 'boolean') f.default = raw.default;
  for (const k of ['minimum', 'maximum', 'minLength', 'maxLength'] as const) if (typeof raw[k] === 'number' && Number.isFinite(raw[k])) f[k] = raw[k] as number;
  if (text(raw.format)) f.format = text(raw.format);
  return f;
}

/** The MCP restricted schema (flat primitives); null when it holds anything Orgus cannot show. */
export function elicitationSchemaOf(raw: unknown): ElicitationSchema | null {
  if (raw === undefined || raw === null) return { properties: {} };
  if (!isObj(raw)) return null;
  const props = raw.properties === undefined ? {} : raw.properties;
  if (!isObj(props)) return null;
  const properties: Record<string, ElicitationField> = {};
  for (const [k, v] of Object.entries(props)) {
    const f = fieldOf(v);
    if (!f) return null;
    properties[k] = f;
  }
  const required = Array.isArray(raw.required) ? raw.required.filter((r): r is string => typeof r === 'string' && r in properties) : [];
  return { properties, ...(required.length ? { required } : {}) };
}

/** The first Markdown heading of a plan, if it has one. */
function headingOf(plan: string): string | null {
  const m = /^\s{0,3}#{1,6}\s+(.+?)\s*#*\s*$/m.exec(plan);
  return m ? m[1]!.trim() : null;
}

/** A short title for the Inbox row. */
export function askTitle(ask: ApprovalAsk): string {
  if (ask.type === 'questions') return oneLine(ask.questions[0]?.question ?? 'Claude has a question', 160);
  if (ask.type === 'plan') {
    const h = headingOf(ask.plan);
    return h ? oneLine(`Approve plan: ${h}`, 160) : 'Approve plan';
  }
  return oneLine(`${ask.server}: ${ask.message || 'asks for input'}`, 160);
}

/**
 * What a hook call asks a person, from its tool input (AskUserQuestion, ExitPlanMode) or its
 * elicitation fields. Null for a tool call, or for an input Orgus cannot show faithfully (the
 * session's own dialog takes it then).
 */
export function askFromHook(event: string, tool: string, input: unknown): { kind: AskKind; ask: ApprovalAsk; title: string } | null {
  const kind = askKindOf(event, tool);
  if (!kind || !isObj(input)) return null;
  let ask: ApprovalAsk;
  if (kind === 'question') {
    const questions = questionsOf(input);
    if (!questions) return null;
    ask = { type: 'questions', questions };
  } else if (kind === 'plan') {
    const plan = text(input.plan);
    // No plan text (Claude Code injects it from the plan file): nothing to approve in Orgus.
    if (!plan.trim()) return null;
    ask = { type: 'plan', plan: plan.length > ASK_PLAN_MAX_CHARS ? `${plan.slice(0, ASK_PLAN_MAX_CHARS)}\n\n[The plan is longer; the rest is in its file.]` : plan, ...(text(input.planFilePath) ? { planFilePath: text(input.planFilePath) } : {}) };
  } else {
    const server = text(input.mcp_server_name) || tool.replace(/^mcp:/, '');
    const url = text(input.url);
    const mode = input.mode === 'url' || (input.mode === undefined && url) ? 'url' : 'form';
    if (mode === 'url' && !/^https?:\/\//i.test(url)) return null;
    const schema = mode === 'form' ? elicitationSchemaOf(input.requested_schema) : null;
    if (mode === 'form' && !schema) return null;
    ask = { type: 'elicitation', server, message: text(input.message).slice(0, MESSAGE_MAX_CHARS), mode, ...(mode === 'url' ? { url } : {}), ...(schema ? { schema } : {}) };
  }
  return { kind, ask, title: askTitle(ask) };
}

export type AskCheck = { ok: true; answer: ApprovalAskAnswer } | { ok: false; why: string; next: string };

const bad = (why: string, next: string): AskCheck => ({ ok: false, why, next });

function checkAnswers(questions: readonly AskQuestion[], given: unknown): AskCheck {
  if (!isObj(given)) return bad('An answer to a question needs answers: each question text with the option it picks.', 'Pick an option for every question and send again.');
  const want = new Set(questions.map((q) => q.question));
  for (const k of Object.keys(given)) {
    if (!want.has(k)) return bad(`"${oneLine(k, 80)}" is not one of the questions Claude asked.`, 'Reload the request and answer the questions it shows.');
  }
  const answers: Record<string, string> = {};
  for (const q of questions) {
    const v = given[q.question];
    if (typeof v !== 'string' || !v.trim()) return bad(`"${oneLine(q.question, 80)}" has no answer.`, 'Pick an option, or write your own answer, for every question.');
    if (v.length > ASK_ANSWER_MAX_CHARS) return bad(`The answer to "${oneLine(q.question, 80)}" is ${v.length} characters; the limit is ${ASK_ANSWER_MAX_CHARS}.`, 'Shorten it and send again.');
    // An option label (several joined with ", " for multiSelect), or else the person's own words.
    answers[q.question] = v.trim();
  }
  return { ok: true, answer: { answers } };
}

function checkValue(key: string, f: ElicitationField, v: unknown): string | null {
  const name = `"${f.title ?? key}"`;
  if (f.type === 'boolean') return typeof v === 'boolean' ? null : `${name} must be true or false.`;
  if (f.type === 'number' || f.type === 'integer') {
    if (typeof v !== 'number' || !Number.isFinite(v)) return `${name} must be a number.`;
    if (f.type === 'integer' && !Number.isInteger(v)) return `${name} must be a whole number.`;
    if (f.minimum !== undefined && v < f.minimum) return `${name} must be at least ${f.minimum}.`;
    if (f.maximum !== undefined && v > f.maximum) return `${name} must be at most ${f.maximum}.`;
    return null;
  }
  if (typeof v !== 'string') return `${name} must be text.`;
  if (f.enum && !f.enum.includes(v)) return `${name} must be one of: ${f.enum.join(', ')}.`;
  if (f.minLength !== undefined && v.length < f.minLength) return `${name} needs at least ${f.minLength} characters.`;
  if (f.maxLength !== undefined && v.length > f.maxLength) return `${name} takes at most ${f.maxLength} characters.`;
  if (v.length > ASK_ANSWER_MAX_CHARS) return `${name} takes at most ${ASK_ANSWER_MAX_CHARS} characters.`;
  if (f.format === 'email' && !/^[^\s@]+@[^\s@]+$/.test(v)) return `${name} must be an email address.`;
  if (f.format === 'uri' && !/^[a-z][a-z0-9+.-]*:\S+$/i.test(v)) return `${name} must be a link (a URI).`;
  if (f.format === 'date' && !/^\d{4}-\d{2}-\d{2}$/.test(v)) return `${name} must be a date (YYYY-MM-DD).`;
  if (f.format === 'date-time' && Number.isNaN(Date.parse(v))) return `${name} must be a date and time.`;
  return null;
}

function checkContent(schema: ElicitationSchema, given: unknown): AskCheck {
  if (!isObj(given)) return bad('Accepting this request needs content: the form values, by field.', 'Fill in the form and send again.');
  for (const k of Object.keys(given)) {
    if (!(k in schema.properties)) return bad(`"${oneLine(k, 80)}" is not a field of this form.`, 'Reload the request and fill in the fields it shows.');
  }
  const content: Record<string, string | number | boolean> = {};
  for (const [k, f] of Object.entries(schema.properties)) {
    const v = given[k];
    if (v === undefined || v === null || v === '') {
      if (schema.required?.includes(k)) return bad(`"${f.title ?? k}" is required.`, 'Fill it in and send again.');
      continue;
    }
    const why = checkValue(k, f, v);
    if (why) return bad(why, 'Correct it and send again.');
    content[k] = v as string | number | boolean;
  }
  return { ok: true, answer: { content } };
}

/**
 * Check a person's answer to a question, plan or elicitation. 'deny' needs nothing more (a
 * question dismissed, a plan sent back, an elicitation declined). 'allow' needs: answers for
 * every question (keyed by the exact question text; a value that is not an option label is the
 * person's own words); an optional planMode; form content that fits the schema (types, required
 * fields, choices, bounds). A URL elicitation's allow means the person opened the link.
 */
export function checkAskAnswer(ask: ApprovalAsk, answer: 'allow' | 'deny', given: unknown): AskCheck {
  if (given !== undefined && given !== null && !isObj(given)) return bad('The answer\'s ask field must be an object.', 'Send answers, planMode or content in it.');
  const g = (given ?? {}) as Partial<Record<keyof ApprovalAskAnswer, unknown>>;
  if (answer === 'deny') return { ok: true, answer: {} };
  if (ask.type === 'questions') return checkAnswers(ask.questions, g.answers);
  if (ask.type === 'plan') {
    if (g.planMode === undefined) return { ok: true, answer: {} };
    if (!PLAN_MODES.includes(g.planMode as PlanContinueMode)) return bad(`planMode must be ${PLAN_MODES.join(' or ')}.`, 'Pick how Claude continues and send again.');
    return { ok: true, answer: { planMode: g.planMode as PlanContinueMode } };
  }
  if (ask.mode === 'url') return { ok: true, answer: {} };
  return checkContent(ask.schema ?? { properties: {} }, g.content ?? (Object.keys(ask.schema?.properties ?? {}).length ? undefined : {}));
}
